/**
 * Expense and income ledgers. The only write path for manual entries: every save computes the
 * accounting month through AccountingPeriodService, validates the record, bumps its version and
 * marks it pending for Excel sync. Nothing is ever hard-deleted — records are voided.
 */
import { computeAccountingMonth } from "@/domain/accounting-period";
import type { IncomeType } from "@/domain/income";
import { duplicateWindow, findDuplicates, type DuplicateMatch, type DuplicateProbe } from "@/domain/duplicates";
import type { DuplicateReview, Expense, Income, YearMonth } from "@/domain/model";
import { emptySyncState } from "@/domain/model";
import { parseAmountInput } from "@/domain/money";
import { assertValid, expenseIssues, incomeIssues } from "@/domain/validation";
import { newId } from "@/lib/hash";
import { nowIso, type BudgetDatabase } from "./db";
import { categorySourceFor, learnFromSave } from "./extraction";
import { loadReferenceContext } from "./reference";

export class LedgerError extends Error {
  override readonly name = "LedgerError";
}

function nextSync(previous: Expense["sync"] | undefined): Expense["sync"] {
  // Any change must reach the workbook again; keep the last synced version for traceability.
  return { ...(previous ?? emptySyncState()), status: "pending", error: null };
}

// ─── Expenses ───────────────────────────────────────────────────────────────────────────────────

export interface ExpenseInput {
  /** Omit to create; pass to edit. */
  id?: string;
  purchaseDate: string;
  /** As typed by the user ("12,50"). */
  amount: string;
  paymentMethodId: string;
  categoryId: string;
  merchant?: string | null;
  note?: string | null;
  /** `YYYY-MM` to override the payment method's rule; empty/null to apply it. */
  accountingMonthOverride?: string | null;
  /**
   * Creating from a persisted draft (e.g. a receipt photo): the draft id becomes the expense id,
   * its image moves to the expense and the draft is deleted — all in the same transaction.
   */
  draftId?: string;
}

/** Preview used by the entry form: which accounting month a purchase will belong to. */
export async function previewExpenseAccountingMonth(
  db: BudgetDatabase,
  purchaseDate: string,
  paymentMethodId: string,
  override?: string | null,
) {
  const method = await db.paymentMethods.get(paymentMethodId);
  if (!method) throw new LedgerError("Metodo di pagamento non trovato.");
  return computeAccountingMonth({ purchaseDate, rule: method.accountingRule, override });
}

/**
 * What to do when the expense being saved looks like an existing one:
 *  - "ask"  (default, interactive) do not save; throw PossibleDuplicateError with the matches so the
 *           user can keep, cancel or edit
 *  - "keep" save and record that the user intentionally kept it (status "kept")
 *  - "flag" save and mark it "possible" for later review (non-interactive paths)
 */
export type DuplicateDecision = "ask" | "keep" | "flag";

export interface DuplicateCandidate {
  match: DuplicateMatch;
  expense: Expense;
}

export class PossibleDuplicateError extends LedgerError {
  constructor(readonly candidates: DuplicateCandidate[]) {
    super(candidates.length === 1 ? "Possibile spesa duplicata." : `Possibile spesa duplicata: ${candidates.length} spese simili.`);
  }
}

/** Existing expenses that look like the probe, best first (read-only). */
export async function findPossibleDuplicates(db: BudgetDatabase, probe: DuplicateProbe): Promise<DuplicateCandidate[]> {
  const { from, to } = duplicateWindow(probe.purchaseDate);
  const nearby = await db.expenses.where("purchaseDate").between(from, to, true, true).toArray();
  const byId = new Map(nearby.map((e) => [e.id, e]));
  return findDuplicates(probe, nearby).map((match) => ({ match, expense: byId.get(match.expenseId)! }));
}

/** Expenses that were saved as possible duplicates *of* this one (reverse lookup, read-only). */
export async function findExpensesFlaggingAsDuplicate(db: BudgetDatabase, id: string): Promise<Expense[]> {
  return db.expenses.filter((e) => e.status !== "void" && e.duplicate.matches.some((m) => m.expenseId === id)).toArray();
}

function reviewFor(
  matches: DuplicateMatch[],
  previous: Expense | undefined,
  decision: DuplicateDecision,
  candidates: () => DuplicateCandidate[],
  now: string,
): DuplicateReview {
  if (matches.length === 0) return { status: "none", matches: [], checkedAt: now, resolvedAt: null };
  // An edit does not ask again about matches the user already accepted.
  const acknowledged = new Set(previous?.duplicate.status === "kept" ? previous.duplicate.matches.map((m) => m.expenseId) : []);
  if (matches.every((m) => acknowledged.has(m.expenseId))) {
    return { status: "kept", matches, checkedAt: now, resolvedAt: previous!.duplicate.resolvedAt };
  }
  if (decision === "ask") throw new PossibleDuplicateError(candidates());
  if (decision === "keep") return { status: "kept", matches, checkedAt: now, resolvedAt: now };
  return { status: "possible", matches, checkedAt: now, resolvedAt: null };
}

export async function saveExpense(
  db: BudgetDatabase,
  input: ExpenseInput,
  { onDuplicate = "ask" }: { onDuplicate?: DuplicateDecision } = {},
): Promise<Expense> {
  const ctx = await loadReferenceContext(db);
  const method = ctx.paymentMethods.get(input.paymentMethodId);
  if (!method) throw new LedgerError("Metodo di pagamento non trovato.");
  const amount = parseAmountInput(input.amount);
  const { accountingMonth, appliedRule } = computeAccountingMonth({
    purchaseDate: input.purchaseDate,
    rule: method.accountingRule,
    override: input.accountingMonthOverride,
  });

  return db.transaction("rw", [db.expenses, db.expenseDrafts, db.attachments, db.extractions, db.merchantAliases], async () => {
    const previous = input.id ? await db.expenses.get(input.id) : undefined;
    if (input.id && !previous) throw new LedgerError("Spesa non trovata.");
    const draft = !input.id && input.draftId ? await db.expenseDrafts.get(input.draftId) : undefined;
    if (input.draftId && !input.id && !draft) throw new LedgerError("Bozza non trovata.");
    if (draft && (await db.expenses.get(draft.id))) throw new LedgerError("Questa bozza è già stata salvata.");
    if (previous?.source === "excel_import") throw new LedgerError("Le righe importate da Excel si modificano nel file Excel.");
    if (previous?.status === "void") throw new LedgerError("La spesa è annullata.");
    const now = nowIso();
    const merchant = input.merchant?.trim() || null;
    const probe: DuplicateProbe = {
      id: previous?.id,
      purchaseDate: input.purchaseDate,
      amountEur: amount,
      paymentMethodId: method.id,
      merchant,
      categoryId: input.categoryId,
    };
    const { from, to } = duplicateWindow(input.purchaseDate);
    const nearby = await db.expenses.where("purchaseDate").between(from, to, true, true).toArray();
    const matches = findDuplicates(probe, nearby);
    const byId = new Map(nearby.map((e) => [e.id, e]));
    const duplicate = reviewFor(matches, previous, onDuplicate, () => matches.map((match) => ({ match, expense: byId.get(match.expenseId)! })), now);
    // What OCR read from the draft's image, if anything (to record the outcome and learn from it).
    const draftImages = draft ? await db.attachments.where("[ownerKind+ownerId]").equals(["draft", draft.id]).primaryKeys() : [];
    const extraction = draftImages.length ? ((await db.extractions.where("attachmentId").anyOf(draftImages).last()) ?? null) : null;

    const record: Expense = {
      id: previous?.id ?? draft?.id ?? newId(),
      version: (previous?.version ?? 0) + 1,
      status: "confirmed",
      source: previous?.source ?? (draft?.origin === "camera" ? "receipt" : draft?.origin === "library" ? "screenshot" : "manual"),
      purchaseDate: input.purchaseDate,
      purchaseDateCertain: true,
      accountingMonth,
      accountingRule: appliedRule,
      paymentMethodId: method.id,
      amount,
      currency: "EUR",
      amountEur: amount,
      categoryId: input.categoryId,
      categorySource: previous
        ? previous.categoryId === input.categoryId
          ? previous.categorySource
          : "user"
        : categorySourceFor(extraction?.status === "done" ? extraction : null, input.categoryId),
      merchant,
      note: input.note?.trim() || null,
      legacy: null,
      confirmedAt: previous?.confirmedAt ?? now,
      voidedAt: null,
      sync: nextSync(previous?.sync),
      duplicate,
      createdAt: previous?.createdAt ?? now,
      updatedAt: now,
    };
    assertValid("Spesa", expenseIssues(record, ctx, { requireActiveCategory: previous?.categoryId !== record.categoryId }));
    await db.expenses.put(record);
    if (draft) {
      await db.attachments
        .where("[ownerKind+ownerId]")
        .equals(["draft", draft.id])
        .modify({ ownerKind: "expense", ownerId: record.id, updatedAt: now });
      await db.expenseDrafts.delete(draft.id);
      if (extraction?.status === "done") await learnFromSave(db, extraction, record);
    }
    return record;
  });
}

/** The user reviewed a "possible" duplicate and keeps it. Both expenses stay untouched otherwise. */
export async function keepPossibleDuplicate(db: BudgetDatabase, id: string): Promise<Expense> {
  return db.transaction("rw", db.expenses, async () => {
    const e = await db.expenses.get(id);
    if (!e) throw new LedgerError("Spesa non trovata.");
    if (e.duplicate.status !== "possible") return e;
    const now = nowIso();
    const record: Expense = {
      ...e,
      duplicate: { ...e.duplicate, status: "kept", resolvedAt: now },
      version: e.version + 1,
      updatedAt: now,
      sync: nextSync(e.sync),
    };
    await db.expenses.put(record);
    return record;
  });
}

export async function voidExpense(db: BudgetDatabase, id: string): Promise<Expense> {
  return db.transaction("rw", db.expenses, async () => {
    const e = await db.expenses.get(id);
    if (!e) throw new LedgerError("Spesa non trovata.");
    if (e.source === "excel_import") throw new LedgerError("Le righe importate da Excel si modificano nel file Excel.");
    if (e.status === "void") return e;
    const now = nowIso();
    const record: Expense = { ...e, status: "void", voidedAt: now, version: e.version + 1, updatedAt: now, sync: nextSync(e.sync) };
    await db.expenses.put(record);
    return record;
  });
}

export async function listExpenses(db: BudgetDatabase, accountingMonth: YearMonth, { includeVoid = false } = {}): Promise<Expense[]> {
  const rows = await db.expenses.where("accountingMonth").equals(accountingMonth).toArray();
  return rows
    .filter((e) => includeVoid || e.status !== "void")
    .sort((a, b) => b.purchaseDate.localeCompare(a.purchaseDate) || b.createdAt.localeCompare(a.createdAt));
}

// ─── Income ─────────────────────────────────────────────────────────────────────────────────────

export interface IncomeInput {
  id?: string;
  date: string;
  amount: string;
  type: IncomeType;
  description: string;
  notes?: string | null;
  accountingMonthOverride?: string | null;
}

export async function saveIncome(db: BudgetDatabase, input: IncomeInput): Promise<Income> {
  const amount = parseAmountInput(input.amount);
  const { accountingMonth, appliedRule } = computeAccountingMonth({
    purchaseDate: input.date,
    rule: "same_month",
    override: input.accountingMonthOverride,
  });
  return db.transaction("rw", db.incomes, async () => {
    const previous = input.id ? await db.incomes.get(input.id) : undefined;
    if (input.id && !previous) throw new LedgerError("Entrata non trovata.");
    if (previous?.source === "excel_import") throw new LedgerError("Le entrate importate da Excel si modificano nel file Excel.");
    if (previous?.status === "void") throw new LedgerError("L'entrata è annullata.");
    const now = nowIso();
    const record: Income = {
      id: previous?.id ?? newId(),
      version: (previous?.version ?? 0) + 1,
      status: "confirmed",
      source: previous?.source ?? "manual",
      date: input.date,
      dateCertain: true,
      accountingMonth,
      accountingRule: appliedRule === "manual_override" ? "manual_override" : "same_month",
      type: input.type,
      description: input.description.trim(),
      amount,
      notes: input.notes?.trim() || null,
      legacy: null,
      voidedAt: null,
      sync: nextSync(previous?.sync),
      createdAt: previous?.createdAt ?? now,
      updatedAt: now,
    };
    assertValid("Entrata", incomeIssues(record));
    await db.incomes.put(record);
    return record;
  });
}

export async function voidIncome(db: BudgetDatabase, id: string): Promise<Income> {
  return db.transaction("rw", db.incomes, async () => {
    const i = await db.incomes.get(id);
    if (!i) throw new LedgerError("Entrata non trovata.");
    if (i.source === "excel_import") throw new LedgerError("Le entrate importate da Excel si modificano nel file Excel.");
    if (i.status === "void") return i;
    const now = nowIso();
    const record: Income = { ...i, status: "void", voidedAt: now, version: i.version + 1, updatedAt: now, sync: nextSync(i.sync) };
    await db.incomes.put(record);
    return record;
  });
}

export async function listIncomes(db: BudgetDatabase, accountingMonth: YearMonth, { includeVoid = false } = {}): Promise<Income[]> {
  const rows = await db.incomes.where("accountingMonth").equals(accountingMonth).toArray();
  return rows.filter((i) => includeVoid || i.status !== "void").sort((a, b) => b.date.localeCompare(a.date));
}

